// @ts-nocheck
//components/auth/auth-modal.tsx
"use client"

import { useState, type FormEvent } from "react"
import { X, Eye, EyeOff, Loader2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Separator } from "@/components/ui/separator"
import { GoogleIcon } from "@/components/auth/social-icons"
import { useAuth } from "@/components/auth/auth-context"
import { useRouter } from 'next/navigation'
import { env } from "@/lib/env";

export function AuthModal({ onClose }: { onClose?: () => void }) {
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [mode, setMode] = useState<"login" | "signup">("login")
  const [rememberMe, setRememberMe] = useState(false)
  const { isLoading } = useAuth()
  const router = useRouter()
  const [localError, setLocalError] = useState<string | null>(null)

async function handleSubmit(e: FormEvent) {
        e.preventDefault()
        if (!email.trim() || !password.trim()) return
        if (mode === "login") {
          router.push("/signin")
        } else {
          router.push("/signup")
        }
        if (onClose) onClose()
      }


  function handleSocialLogin(provider: string) {
    window.location.href = `${env.apiUrl}/auth/${provider}`
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-foreground/20 backdrop-blur-sm" />

      {/* Modal */}
      <div className="relative z-10 w-full max-w-md rounded-2xl bg-card shadow-xl border border-border">

        {/* Close button */}
        <div className="flex justify-end p-4 pb-0">
          <button
            onClick={onClose}
            className="rounded-full p-1.5 text-muted-foreground hover:bg-accent transition-colors"
            aria-label="Close"
          >
            <X className="size-5" />
          </button>
        </div>

        {/* Header */}
        <div className="flex flex-col items-center px-6 pb-2 pt-0">
          <h1 className="text-2xl font-semibold tracking-tight text-card-foreground text-balance text-center">
            {mode === "login" ? "Log in or sign up" : "Create your account"}
          </h1>
          <p className="mt-2 text-center text-sm leading-relaxed text-muted-foreground">
            {"You'll get smarter responses and can upload files, images, and more."}
          </p>
        </div>

        {/* Social buttons — только Google */}
        <div className="flex flex-col gap-3 px-6 py-4">
          <Button
            variant="outline"
            className="h-12 w-full justify-center gap-3 rounded-xl border-border bg-card text-card-foreground hover:bg-accent text-sm font-medium transition-colors"
            onClick={() => handleSocialLogin("google")}
            type="button"
          >
            <GoogleIcon />
            Continue with Google
          </Button>
        </div>

        {/* OR Divider */}
        <div className="flex items-center gap-4 px-6 py-2">
          <Separator className="flex-1" />
          <span className="text-xs font-medium text-muted-foreground tracking-wider">OR</span>
          <Separator className="flex-1" />
        </div>

        {/* Email + Password form */}
        <form onSubmit={handleSubmit} className="flex flex-col gap-3 px-6 pt-2 pb-4">
          <Input
            type="email"
            placeholder="Email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="h-12 rounded-xl border-border bg-card text-card-foreground placeholder:text-muted-foreground"
            required
            disabled={isLoading}
          />

          <div className="relative">
            <Input
              type={showPassword ? "text" : "password"}
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="h-12 rounded-xl border-border bg-card text-card-foreground placeholder:text-muted-foreground pe-12"
              required
              disabled={isLoading}
              minLength={8}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute end-3 top-3 text-muted-foreground hover:text-foreground transition-colors"
            >
              {showPassword ? <EyeOff className="size-5" /> : <Eye className="size-5" />}
            </button>
          </div>

          <div className="flex items-center justify-between px-1">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="rememberMe"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="size-4 rounded border-border"
              />
              <label htmlFor="rememberMe" className="text-sm text-muted-foreground">
                Запомнить меня
              </label>
            </div>
            <button
              type="button"
              onClick={() => router.push('/recovery')}
              className="text-sm text-muted-foreground underline underline-offset-4 hover:opacity-70"
            >
              Забыл пароль
            </button>
          </div>

          {localError && (
            <p className="text-sm text-destructive text-center px-2">{localError}</p>
          )}

          <Button
            type="submit"
            className="h-12 w-full rounded-xl text-base font-medium"
            disabled={isLoading || !email.trim() || !password.trim()}
          >
            {isLoading
              ? <Loader2 className="size-5 animate-spin" />
              : mode === "login" ? "Log in" : "Create account"
            }
          </Button>
        </form>

        {/* Toggle mode */}
        <div className="px-6 pb-4 text-center">
          {mode === "login" ? (
            <p className="text-sm text-muted-foreground">
              {"Don't have an account? "}
              <button
                onClick={() => setMode("signup")}
                className="font-medium text-card-foreground underline underline-offset-4 hover:opacity-70 transition-opacity"
              >
                Sign up
              </button>
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">
              Already have an account?{" "}
              <button
                onClick={() => setMode("login")}
                className="font-medium text-card-foreground underline underline-offset-4 hover:opacity-70 transition-opacity"
              >
                Log in
              </button>
            </p>
          )}
        </div>

        {/* Terms */}
        <div className="border-t border-border px-6 py-4">
          <p className="text-center text-xs leading-relaxed text-muted-foreground">
            By continuing, you agree to our{" "}
            <a href="#" className="underline underline-offset-2 hover:text-card-foreground transition-colors">
              Terms of Service
            </a>{" "}
            and have read our{" "}
            <a href="#" className="underline underline-offset-2 hover:text-card-foreground transition-colors">
              Privacy Policy
            </a>.
          </p>
        </div>
      </div>
    </div>
  )
}